import { ResponsiveBar } from '@nivo/bar';
import React, { useState } from 'react';
import { TwTheme } from '../../../../Tw';
import {
  getSequentialColor,
  QualitativePaletteId,
  SequentialPaletteId,
} from '../../../common/color.helper';
import ToolTip from '../../../layout/tooltip/ToolTip';
import { ThresholdRange } from '../kpiModel';

const themeColors = TwTheme.extend.colors;

// interface Benchmarks {
//   target?: number;
//   warning?: { lower?: number; upper?: number };
//   critical?: { lower?: number; upper?: number };
// }

interface KpiBarChartProps {
  data: number[];
  thresholds?: ThresholdRange[];
  showTrendLine?: boolean;
  unit?: string;
  mini?: boolean;
  legend?: string;
  title?: string;

  /**
   * Optional palette ID for sequential color mapping (e.g., 'elevation-sort')
   */
  palette?: SequentialPaletteId | QualitativePaletteId;
  sequentialPaletteId?: string;
  /**
   * Optional trend array (e.g., ['up', 'down', ...]) to color bars by trend
   */
  trends?: ('up' | 'down' | 'stable')[];
  /**
   * Metric identifier to determine appropriate palette and formatting
   */
  metricId?: string;

  /**
   * Show legend/title above the chart
   */
  showTitle?: boolean;
}

type OverlayOption = 'Target' | 'Warning' | 'Critical' | 'Trend Line';

const calculateTrendLine = (data: number[]) => {
  // Simple linear regression for trend line
  const n = data.length;
  const xSum = data.reduce((sum, _, i) => sum + i, 0);
  const ySum = data.reduce((sum, y) => sum + y, 0);
  const xySum = data.reduce((sum, y, i) => sum + i * y, 0);
  const xxSum = data.reduce((sum, _, i) => sum + i * i, 0);
  const slope = (n * xySum - xSum * ySum) / (n * xxSum - xSum * xSum);
  const intercept = (ySum - slope * xSum) / n;
  return data.map((_, i) => slope * i + intercept);
};

// Metric-specific palette and unit mappings
const getMetricPalette = (metricId?: string): SequentialPaletteId => {
  const metricPalettes: Record<string, SequentialPaletteId> = {
    uomLineRatio: 'trendAscending',
    jobLineRatio: 'heatmap-analytics',
    orderLineRatio: 'velocity-analytics',
    idleTimeMs: 'trendDescending', // Red for high idle time (bad)
    grossTimeMs: 'elevation-sort',
    netLinesPerformance: 'trendAscending', // Green for high performance (good)
    netUomsPerformance: 'trendAscending',
    netLocationsPerformance: 'trendAscending',
    totalEmployeeActivityDuration: 'signal-strength',
  };
  return metricPalettes[metricId || ''] || 'trendAscending';
};

const getMetricUnit = (metricId?: string): string => {
  const metricUnits: Record<string, string> = {
    uomLineRatio: 'ratio',
    jobLineRatio: 'ratio',
    orderLineRatio: 'ratio',
    idleTimeMs: 'ms',
    grossTimeMs: 'ms',
    netLinesPerformance: '%',
    netUomsPerformance: '%',
    netLocationsPerformance: '%',
    totalEmployeeActivityDuration: 'ms',
  };
  return metricUnits[metricId || ''] || '';
};

const getMetricTitle = (metricId?: string): string => {
  const metricTitles: Record<string, string> = {
    uomLineRatio: 'UOM Line Ratio',
    jobLineRatio: 'Job Line Ratio',
    orderLineRatio: 'Order Line Ratio',
    idleTimeMs: 'Idle Time',
    grossTimeMs: 'Gross Time',
    netLinesPerformance: 'Net Lines Performance',
    netUomsPerformance: 'Net UOMs Performance',
    netLocationsPerformance: 'Net Locations Performance',
    totalEmployeeActivityDuration: 'Employee Activity Duration',
  };
  return metricTitles[metricId || ''] || 'Metric';
};

export const KpiBarChart: React.FC<KpiBarChartProps> = ({
  data: trendData,
  thresholds: benchmarks = {},
  showTrendLine = true,
  unit,
  mini = false,
  sequentialPaletteId,
  trends,
  metricId,
  title,
  showTitle = true,
}) => {
  const [overlayOptions, setOverlayOptions] = useState<OverlayOption[]>([
    'Target',
    'Trend Line',
  ]);

  const overlayValues: OverlayOption[] = [
    'Target',
    'Warning',
    'Critical',
    'Trend Line',
  ];

  // Prepare bar data
  const barData = trendData.map((y, i) => ({
    day: i + 1,
    value: y,
  }));

  // Determine the actual unit and title to use
  const displayUnit = unit || getMetricUnit(metricId);
  const displayTitle = title || getMetricTitle(metricId);
  const activePalette =
    (sequentialPaletteId as SequentialPaletteId) || getMetricPalette(metricId);

  // Debug logging
  console.log(`KpiBarChart ${metricId}:`, {
    dataLength: trendData.length,
    barData: barData.slice(0, 5), // Show first 5 items
    displayUnit,
    displayTitle,
    activePalette,
  });

  // Helper to get color from color.helper.ts
  const getPaletteColor = (v: number) => {
    // v should be in [0, 100], so scale index accordingly
    const percent = Math.round((v / (trendData.length - 1 || 1)) * 100);
    return getSequentialColor(percent, activePalette)[0];
  };

  // Color logic: by trend, by benchmark, or palette
  const getBarColor = (d: { value: number; day: number }, i: number) => {
    // Color by trend if provided
    if (trends && trends[i]) {
      if (trends[i] === 'up') return themeColors.trend.up;
      if (trends[i] === 'down') return themeColors.trend.down;
      if (trends[i] === 'stable') return themeColors.trend.stable;
    }
    // Color by benchmark thresholds if available
    // if (benchmarks) {
    //   if (
    //     benchmarks.critical &&
    //     ((benchmarks.critical.lower !== undefined &&
    //       d.value <= benchmarks.critical.lower) ||
    //       (benchmarks.critical.upper !== undefined &&
    //         d.value >= benchmarks.critical.upper))
    //   ) {
    //     return themeColors.benchmark.critical;
    //   }
    //   if (
    //     benchmarks.warning &&
    //     ((benchmarks.warning.lower !== undefined &&
    //       d.value <= benchmarks.warning.lower) ||
    //       (benchmarks.warning.upper !== undefined &&
    //         d.value >= benchmarks.warning.upper))
    //   ) {
    //     return themeColors.benchmark.warning;
    //   }
    //   if (benchmarks.target !== undefined && d.value === benchmarks.target) {
    //     return themeColors.benchmark.target;
    //   }
    // }
    // Fallback to palette from color.helper.ts
    return getPaletteColor(i);
  };

  // Prepare overlays
  const overlays: {
    id: string;
    data: { x: number; y: number }[];
    color: string;
  }[] = [];
  // if (
  //   overlayOptions.includes('Target') &&
  //   benchmarks &&
  //   benchmarks.target !== undefined
  // ) {
  //   overlays.push({
  //     id: 'Target',
  //     data: barData.map(d => ({ x: d.day, y: benchmarks.target! })),
  //     color: themeColors.benchmark.target, // green
  //   });
  // }
  // if (overlayOptions.includes('Warning') && benchmarks.warning) {
  //   if (benchmarks.warning.lower !== undefined) {
  //     overlays.push({
  //       id: 'Warning Lower',
  //       data: barData.map(d => ({ x: d.day, y: benchmarks.warning!.lower! })),
  //       color: themeColors.benchmark.warning, // yellow
  //     });
  //   }
  //   if (benchmarks.warning.upper !== undefined) {
  //     overlays.push({
  //       id: 'Warning Upper',
  //       data: barData.map(d => ({ x: d.day, y: benchmarks.warning!.upper! })),
  //       color: themeColors.benchmark.warning, // yellow
  //     });
  //   }
  // }
  // if (overlayOptions.includes('Critical') && benchmarks.critical) {
  //   if (benchmarks.critical.lower !== undefined) {
  //     overlays.push({
  //       id: 'Critical Lower',
  //       data: barData.map(d => ({ x: d.day, y: benchmarks.critical!.lower! })),
  //       color: themeColors.benchmark.critical, // red
  //     });
  //   }
  //   if (benchmarks.critical.upper !== undefined) {
  //     overlays.push({
  //       id: 'Critical Upper',
  //       data: barData.map(d => ({ x: d.day, y: benchmarks.critical!.upper! })),
  //       color: themeColors.benchmark.critical, // red
  //     });
  //   }
  // }
  // Trend line
  const trendLine = overlayOptions.includes('Trend Line')
    ? calculateTrendLine(trendData)
    : null;

  // DropdownSelector for overlays
  const handleOverlayChange = (option: OverlayOption) => {
    setOverlayOptions(prev =>
      prev.includes(option)
        ? prev.filter(o => o !== option)
        : [...prev, option],
    );
  };

  return (
    <div
      style={{
        height: mini ? 40 : showTitle ? 140 : 120,
        width: '100%',
        position: 'relative',
      }}
    >
      {/* Title/Legend */}
      {showTitle && !mini && (
        <div className="mb-2 text-center">
          <div className="text-menu-text text-sm font-medium">
            {displayTitle}
          </div>
          {displayUnit && (
            <div className="text-menu-text text-xs opacity-70">
              ({displayUnit})
            </div>
          )}
        </div>
      )}
      {/* <div>
        <DropdownSelector
          label={'Overlays'}
          values={overlayValues}
          value={null}
          renderValue={option => (
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={overlayOptions.includes(option)}
                readOnly
                className="mr-2"
              />
              {option}
            </div>
          )}
          onChange={handleOverlayChange}
          panelMode
          className="mb-4 w-40"
        />
      </div> */}
      <ResponsiveBar
        data={barData}
        keys={['value']}
        indexBy="day"
        margin={
          mini
            ? { top: 5, right: 5, bottom: 10, left: 5 }
            : { top: 10, right: 10, bottom: 20, left: 10 }
        }
        padding={0.2}
        colors={({ data, index }) => getBarColor(data, index)}
        axisBottom={
          mini
            ? null
            : {
                tickSize: 0,
                tickPadding: 0,
                tickValues:
                  barData.length <= 10
                    ? barData.map(d => d.day)
                    : [
                        barData[0]?.day,
                        barData[Math.floor(barData.length / 2)]?.day,
                        barData[barData.length - 1]?.day,
                      ],
                format: (value: number) => {
                  return `${value}`;
                },
                legend: 'Hour',
                legendPosition: 'middle',
                legendOffset: 14,
              }
        }
        // axisLeft={{
        //   tickSize: 0,
        //   tickPadding: 5,
        //   legend: unit || '',
        //   legendPosition: 'middle',
        //   legendOffset: -40,
        // }}
        enableLabel={false}
        gridYValues={mini ? 0 : 1}
        tooltip={({ id, value, indexValue }) => {
          // Format the value based on the metric type
          let formattedValue: string;
          if (displayUnit === '%') {
            formattedValue = `${(value * 100).toFixed(1)}%`;
          } else if (displayUnit === 'ratio') {
            formattedValue = value.toFixed(3);
          } else if (displayUnit === 'ms') {
            formattedValue = Math.round(value).toLocaleString();
          } else {
            formattedValue = value.toFixed(2);
          }

          return (
            <ToolTip label={`Hour ${indexValue}`} value={formattedValue} />
          );
        }}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: themeColors.chart.ticks,
              },
            },
            legend: {
              text: {
                fill: themeColors.chart.legend,
              },
            },
          },
          grid: {
            line: {
              stroke: themeColors.chart.grid,
              strokeWidth: 0.3,
            },
          },
        }}
        animate={!mini}
        layers={[
          'grid',
          'axes',
          'bars',
          // Overlay lines
          ...overlays.map(overlay => props => (
            <g key={overlay.id}>
              {overlay.data.map((d, i) => {
                // x is a number, so use as is
                const xScale = props.xScale as any;
                const bandwidth =
                  typeof xScale.bandwidth === 'function'
                    ? xScale.bandwidth()
                    : 0;
                const x = props.xScale(d.x) + bandwidth / 2;
                const y = props.yScale(d.y);
                return (
                  <line
                    key={i}
                    x1={x - bandwidth / 2}
                    x2={x + bandwidth / 2}
                    y1={y}
                    y2={y}
                    stroke={overlay.color}
                    strokeWidth={mini ? 0.5 : 1}
                    strokeDasharray={mini ? '2 2' : '3 3'}
                  />
                );
              })}
            </g>
          )),
          // Trend line
          props =>
            trendLine && (
              <g key="trend-line">
                {trendLine.map((y, i) => {
                  if (i === 0) return null;
                  const xScale = props.xScale as any;
                  const bandwidth =
                    typeof xScale.bandwidth === 'function'
                      ? xScale.bandwidth()
                      : 0;
                  const x1 = props.xScale(i) + bandwidth / 2;
                  const x2 = props.xScale(i + 1) + bandwidth / 2;
                  const y1 = props.yScale(trendLine[i - 1]);
                  const y2 = props.yScale(y);

                  return (
                    <line
                      key={i}
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={themeColors.trend.down}
                      strokeWidth={mini ? 0.5 : 1}
                    />
                  );
                })}
              </g>
            ),
        ]}
      />
    </div>
  );
};

export default KpiBarChart;
